import * as XLSX from "xlsx-js-style";

const COLUMN_WIDTHS = {
  "S/N": 6,
  Category: 12,
  Type: 14,
  Date: 18,
  Amount: 14,
  "Previous balance": 16,
  Movement: 14,
  "New balance": 16,
  "Bank / channel": 18,
  Reference: 40,
  "Approved by": 18,
};

const CENTERED_COLUMNS = [
  "S/N",
  "Amount",
  "Previous balance",
  "Movement",
  "New balance",
];

/**
 * Export short-term billing rows to .xlsx (styled header; optional GRAND TOTAL row via Category column).
 * @param {Array<Record<string, unknown>>} data - Rows from ShortTermBillingModal export builder
 * @param {string} name - File name without extension
 * @param {string} [sheetName='Short-term billing'] - Worksheet tab label (max 31 chars in Excel)
 */
export default function exportShortTermBillingExcel(
  data,
  name,
  sheetName = "Short-term billing"
) {
  if (!data?.length) return;

  const worksheet = XLSX.utils.json_to_sheet(data);
  const headers = Object.keys(data[0]);

  worksheet["!cols"] = headers.map((key) => ({
    wch: COLUMN_WIDTHS[key] || 15,
  }));

  headers.forEach((key, colIndex) => {
    const cellAddress = XLSX.utils.encode_cell({ r: 0, c: colIndex });
    if (!worksheet[cellAddress]) worksheet[cellAddress] = { t: "s", v: key };
    worksheet[cellAddress].s = {
      font: { bold: true },
      fill: { fgColor: { rgb: "a7bdd8" } },
      alignment: { horizontal: "center", vertical: "center" },
    };
  });

  data.forEach((row, rowIndex) => {
    const isTotalRow = row.Category === "GRAND TOTAL";

    headers.forEach((key, colIndex) => {
      const cellAddress = XLSX.utils.encode_cell({
        r: rowIndex + 1,
        c: colIndex,
      });
      const value = row[key];
      const isNumber = typeof value === "number" && !isNaN(value);

      if (!worksheet[cellAddress]) {
        worksheet[cellAddress] = {
          t: isNumber ? "n" : "s",
          v: value,
        };
      }

      worksheet[cellAddress].s = {
        alignment: {
          horizontal: CENTERED_COLUMNS.includes(key) ? "center" : "left",
          vertical: "center",
        },
        ...(isTotalRow
          ? {
              font: { bold: true },
              fill: { fgColor: { rgb: "E8F4EC" } },
            }
          : {}),
      };
    });
  });

  const workbook = XLSX.utils.book_new();
  const safeSheet =
    (sheetName || "Sheet1")
      .replace(/[/\\?*:]/g, " ")
      .replace(/\[/g, " ")
      .replace(/\]/g, " ")
      .trim()
      .slice(0, 31) || "Sheet1";
  XLSX.utils.book_append_sheet(workbook, worksheet, safeSheet);

  const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });

  const blob = new Blob([excelBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", `${name}.xlsx`);
  document.body.appendChild(link);
  link.click();

  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}
